// hardcoded-korean-exception: admin 매뉴얼 페이지 — 운영팀 ko 문서 성격 (Cycle 62 정책).
//
// 본 페이지 (AdminBulkImportHelp) 는 ADMIN/SYSTEM_ADMIN role 만 접근 가능한 내부 운영 매뉴얼.
// 일반 사용자 가시 영역이 아닌 운영팀 ko 작성·유지 문서로, 100+ 한국어 strings × 3 lang 적용은
// 비용 ↑ 효익 ↓ (외부 다국어 운영팀이 본 매뉴얼을 다른 언어로 보지 않음). cycle 55-61 의 i18n
// 누적 적용 (lint 39→6) 중 본 페이지만 운영 문서 성격으로 의도적 제외.
//
// 향후 운영팀 다국어화 요구 발생 시: lib/help-i18n.ts 신규 도입 + sections/codes/faq 분할 적용.
// cycle 58 의 graduation 임계 ('4+ components 또는 cross-component 키 공유 → lib') 와 별개로,
// 단일 component 의 대형 문서는 운영 정책 결정에 따름.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BULK_IMPORT_CODE_DOC,
  type BulkImportErrorCode,
} from '@workchat/shared-types';
import { useAuthStore } from '../../store/authStore';

/**
 * Admin Bulk Import (User CSV) 매뉴얼 페이지.
 *
 * URL: `/help/admin/bulk-import` (AuthGuard 안)
 * Anchor: `#deleted-email`, `#institution-not-found`, `#fk-violation`, `#password-hash-failed`
 *         (AdminBulkImportUserSection 의 CODE_GUIDE.actionHref 가 도달하는 위치)
 *
 * 정책:
 *   - admin 외 사용자가 직접 URL 로 접근하면 안내 메시지 (AuthGuard 가 인증만 보장 — role 가드는 본 페이지)
 *   - hash navigation 시 scrollIntoView + focus (a11y 보강)
 *   - 본 cycle 의 핵심: F4 placeholder (`(준비 중)`) 의 정식 해소 — link 활성화로 admin 이 매뉴얼 도달
 */

const ADMIN_ROLES = ['ADMIN', 'SYSTEM_ADMIN'] as const;

/**
 * G7/H1 봉합: 4 anchor id 의 SSOT.
 *
 * 종전 — `<section id="...">` 4곳 + TOC `<a href="#...">` 4곳 + 표 안 `<a href="#...">` 4곳 +
 * AdminBulkImportUserSection.tsx CODE_GUIDE 4 actionHref = 16 hardcode → drift 시 silent UX 위반
 * (link 클릭 → 페이지 로드 OK + element null + 페이지 상단 멈춤).
 *
 * 본 const 가 SSOT — 두 컴포넌트가 import 해 사용. naming 변경 시 1 군데만 수정하면 16곳 자동 일관.
 */
export const BULK_IMPORT_HELP_ANCHORS = {
  deletedEmail: 'deleted-email',
  institutionNotFound: 'institution-not-found',
  fkViolation: 'fk-violation',
  passwordHashFailed: 'password-hash-failed',
} as const;

/** Help 페이지 URL prefix — actionHref 조립용. */
export const BULK_IMPORT_HELP_PATH = '/help/admin/bulk-import';

/** AdminBulkImportUserSection.tsx 가 사용할 actionHref helper. */
export function bulkImportHelpHref(anchor: keyof typeof BULK_IMPORT_HELP_ANCHORS): string {
  return `${BULK_IMPORT_HELP_PATH}#${BULK_IMPORT_HELP_ANCHORS[anchor]}`;
}

/**
 * T5: 4 강조 코드를 BULK_IMPORT_HELP_ANCHORS 매핑.
 * 매뉴얼 표의 후속 조치 컬럼에서 anchor link 표시할 때 사용. 나머지 10 코드는 plain text.
 */
const ANCHOR_BY_CODE: Partial<Record<BulkImportErrorCode, string>> = {
  DELETED_EMAIL_EXISTS: BULK_IMPORT_HELP_ANCHORS.deletedEmail,
  INSTITUTION_NOT_FOUND: BULK_IMPORT_HELP_ANCHORS.institutionNotFound,
  FK_VIOLATION: BULK_IMPORT_HELP_ANCHORS.fkViolation,
  PASSWORD_HASH_FAILED: BULK_IMPORT_HELP_ANCHORS.passwordHashFailed,
};

export function AdminBulkImportHelp() {
  const role = useAuthStore((s) => s.user?.role);
  // P9/R-3 봉합: 명시적 boolean cast — `role && includes(role)` 은 false-y 시 undefined 반환 (truthy 분기엔 문제 없으나 type 정확성 위해).
  const isAdmin = role !== undefined && (ADMIN_ROLES as ReadonlyArray<string>).includes(role);
  const { hash } = useLocation();

  // hash navigation — SPA 라우터의 default 가 anchor scroll 안 함. mount/hash-change 시 보강.
  // G7/A-1 봉합: scrollIntoView + tabindex + focus — 키보드/스크린리더 사용자 anchor 도달 후
  // 다음 Tab 이 페이지 최상단부터 시작하지 않고 anchor 섹션에서 이어짐 (a11y).
  useEffect(() => {
    if (!hash) return;
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    // microtask 후 scroll — 컨텐츠 mount 직후라야 element 존재.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [hash]);

  if (!isAdmin) {
    // P6/A-4 봉합: 정적 안내는 role='status' (aria-live=polite) 가 적합 — alert 는 즉각 주의 (aria-live=assertive).
    // P7/A-5 봉합: 비-admin 분기에 h1 — 페이지 heading hierarchy 보존.
    return (
      <div className="help-non-admin-notice" role="status">
        <h1>관리자 전용 매뉴얼</h1>
        <p>이 페이지는 ADMIN/SYSTEM_ADMIN 권한 보유자만 열람 가능합니다.</p>
        <p>일반 사용자는 시스템 관리자에게 문의해주세요.</p>
      </div>
    );
  }

  return (
    <article aria-label="Admin Bulk Import 매뉴얼">
      <h1>User CSV 일괄 등록 매뉴얼</h1>
      <p>ADMIN 이 사용자 계정을 CSV 파일로 일괄 생성하는 도구의 사용 가이드와 오류 코드 안내입니다.</p>

      <nav className="help-toc" aria-label="목차">
        <div className="help-toc__title">목차</div>
        <ul>
          <li><a href="#overview">개요</a></li>
          <li><a href="#csv-format">CSV 형식 + 제한</a></li>
          <li><a href="#permissions">권한·보안</a></li>
          <li><a href="#error-codes">오류 코드 안내 (14종)</a></li>
          <li><a href={`#${BULK_IMPORT_HELP_ANCHORS.deletedEmail}`}>- DELETED_EMAIL_EXISTS (탈퇴 사용자 충돌)</a></li>
          <li><a href={`#${BULK_IMPORT_HELP_ANCHORS.institutionNotFound}`}>- INSTITUTION_NOT_FOUND</a></li>
          <li><a href={`#${BULK_IMPORT_HELP_ANCHORS.fkViolation}`}>- FK_VIOLATION</a></li>
          <li><a href={`#${BULK_IMPORT_HELP_ANCHORS.passwordHashFailed}`}>- PASSWORD_HASH_FAILED</a></li>
          <li><a href="#faq">자주 묻는 질문</a></li>
        </ul>
      </nav>

      <section id="overview" className="help-section">
        <h2>개요</h2>
        <p>
          <code>설정 → User 일괄 등록 (CSV)</code> 메뉴에서 CSV 파일을 업로드하면 행 단위로 사용자 계정을
          생성합니다. 응답은 행별 성공/실패 결과 (HTTP 200 또는 207 Multi-Status) 로 반환됩니다.
        </p>
        <p>
          본 작업은 <strong>idempotent</strong> 합니다 — 같은 CSV 를 두 번 업로드해도 이미 존재하는 email 은
          <code>EMAIL_EXISTS</code> 로 skip 되고 신규 행만 생성됩니다.
        </p>
      </section>

      <section id="csv-format" className="help-section">
        <h2>CSV 형식 + 제한</h2>
        <h3>필수 컬럼</h3>
        <ul>
          <li><code>email</code> — 최대 255자, ASCII 만 (한국어/유니코드 차단)</li>
          <li><code>name</code> — 최대 50자</li>
        </ul>
        <h3>선택 컬럼</h3>
        <ul>
          <li><code>role</code> — <code>ADMIN</code> | <code>MANAGER</code> | <code>STAFF</code> (default <code>STAFF</code>). <code>SYSTEM_ADMIN</code> / <code>AI_BOT</code> 은 bulk import 금지.</li>
          <li><code>position</code> — 직책</li>
          <li><code>phone</code> — 전화번호</li>
          <li><code>password</code> — 8자 이상. 미설정 시 사용자는 별도 초기 비밀번호 또는 invite 절차 필요 (login 차단 상태로 생성).</li>
        </ul>
        <h3>제한</h3>
        <ul>
          <li>최대 행 수: <strong>10,000</strong></li>
          <li>파일 크기: <strong>5 MB</strong></li>
          <li>요청 한도: 분당 3회 (rate limit, admin 별)</li>
          <li><code>institutionId</code> 는 자동 (현재 admin 의 기관) — CSV/body 무시</li>
        </ul>
      </section>

      <section id="permissions" className="help-section">
        <h2>권한·보안</h2>
        <ul>
          <li>접근 권한: <code>ADMIN</code> 또는 <code>SYSTEM_ADMIN</code> 만</li>
          <li>모든 작업은 <code>audit log</code> 에 기록 (eventType=<code>user.bulk_imported</code>)</li>
          <li>작업 중간에 클라이언트가 abort 하면 진행된 행만 생성 후 중단 (응답에 <code>aborted: true</code>)</li>
          <li>password 평문은 audit log 에 포함되지 않음 (즉시 bcrypt hash)</li>
        </ul>
      </section>

      <section id="error-codes" className="help-section">
        <h2>오류 코드 안내</h2>
        <p>응답의 <code>errors[]</code> 항목은 행 단위 실패를 표시합니다. 코드별 의미와 후속 조치는 다음과 같습니다.</p>

        <table className="help-code-table">
          <thead>
            <tr>
              <th scope="col">코드</th>
              <th scope="col">의미</th>
              <th scope="col">후속 조치</th>
            </tr>
          </thead>
          <tbody>
            {/* T5: 14 hardcoded tr 제거 → BULK_IMPORT_CODE_DOC 에서 derive (cross-package SSOT). */}
            {(Object.keys(BULK_IMPORT_CODE_DOC) as BulkImportErrorCode[]).map((code) => {
              const { meaning, nextAction } = BULK_IMPORT_CODE_DOC[code];
              const anchor = ANCHOR_BY_CODE[code];
              return (
                <tr key={code}>
                  <td><code>{code}</code></td>
                  <td>{meaning}</td>
                  <td>
                    {anchor ? (
                      <a href={`#${anchor}`}>아래 안내 참조</a>
                    ) : (
                      nextAction
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section
        id={BULK_IMPORT_HELP_ANCHORS.deletedEmail}
        className="help-section help-section--highlighted"
        role="region"
        aria-label="자주 발생하는 오류 코드 — DELETED_EMAIL_EXISTS"
      >
        <h2>DELETED_EMAIL_EXISTS — 탈퇴 사용자 email 충돌</h2>
        <p>
          이전에 동일 email 로 가입했다가 탈퇴한 사용자 계정이 데이터베이스에 남아있을 때 발생합니다. 본 시스템은
          <code>soft-delete</code> 정책 (계정을 삭제 표시만 하고 데이터 보존) 을 사용하므로, 탈퇴한 사용자의
          email 은 같은 institution 안에서 즉시 재사용 불가합니다.
        </p>
        <h3>해결 방법</h3>
        <ol>
          <li><strong>다른 email 사용</strong> — 새 사용자에게 다른 email 발급 (가장 단순)</li>
          <li><strong>hard-delete 후 재등록</strong> — 운영팀에 SYSTEM_ADMIN 권한 요청해서 해당 email 의 탈퇴 계정을 영구 삭제 후 재업로드. 단 audit log 보존 정책에 따라 일정 기간 (예: 90일) 안에는 hard-delete 가 제한될 수 있음.</li>
        </ol>
        <h3>참고</h3>
        <p>
          이 코드는 일반 <code>EMAIL_EXISTS</code> 와 분리되어 admin 이 active vs deleted 충돌을 빠르게 구분할 수 있도록
          설계됐습니다 (직전 cycle <code>bulk-import-hardening</code> review C4 봉합).
        </p>
      </section>

      <section
        id={BULK_IMPORT_HELP_ANCHORS.institutionNotFound}
        className="help-section help-section--highlighted"
        role="region"
        aria-label="자주 발생하는 오류 코드 — INSTITUTION_NOT_FOUND"
      >
        <h2>INSTITUTION_NOT_FOUND — institution 비존재·비활성</h2>
        <p>
          ADMIN 이 속한 institution 이 데이터베이스에서 비활성화됐거나 삭제된 경우 발생합니다.
        </p>
        <h3>해결 방법</h3>
        <ol>
          <li><strong>institution 설정 확인</strong> — 시스템 설정 페이지에서 현재 institution 의 활성 상태 확인</li>
          <li><strong>운영팀 문의</strong> — institution 활성화 또는 다른 institution 으로 이전 요청</li>
        </ol>
        <p>
          이 코드는 server 의 Prisma <code>P2025</code> 또는 <code>P2003</code> 가 institution FK 위반에서 발생할 때
          명시적 분류로 매핑됩니다.
        </p>
      </section>

      <section
        id={BULK_IMPORT_HELP_ANCHORS.fkViolation}
        className="help-section help-section--highlighted"
        role="region"
        aria-label="자주 발생하는 오류 코드 — FK_VIOLATION"
      >
        <h2>FK_VIOLATION — foreign key 제약 위반</h2>
        <p>
          <code>INSTITUTION_NOT_FOUND</code> 외의 다른 FK 깨짐 (예: 향후 추가될 department FK, role FK 등) 일 때
          발생합니다. 일반적으로 admin 측에서 직접 해결할 수 없는 server-side 데이터 정합성 이슈입니다.
        </p>
        <h3>해결 방법</h3>
        <ol>
          <li><strong>server 로그 확인</strong> — 운영팀이 logger (Loki/CloudWatch) 에서 정확한 위반 컬럼 추적</li>
          <li><strong>운영팀 문의</strong> — 본 코드는 보통 schema 변경 직후 데이터 마이그 누락 시 발생</li>
        </ol>
      </section>

      <section
        id={BULK_IMPORT_HELP_ANCHORS.passwordHashFailed}
        className="help-section help-section--highlighted"
        role="region"
        aria-label="자주 발생하는 오류 코드 — PASSWORD_HASH_FAILED"
      >
        <h2>PASSWORD_HASH_FAILED — bcrypt 처리 실패</h2>
        <p>
          입력된 password 를 bcrypt 로 hash 하는 과정에서 실패한 경우 발생합니다. 일반적으로 시스템 부하 또는
          매우 긴 password (72 byte 초과 — bcrypt 의 입력 한도) 일 때 가능합니다.
        </p>
        <h3>해결 방법</h3>
        <ol>
          <li><strong>password 길이 확인</strong> — 영문 기준 72자 이하로 작성</li>
          <li><strong>password 컬럼 비워두기</strong> — 사용자가 별도 초기 비밀번호 설정하도록 (login 차단 상태 OK)</li>
          <li><strong>server 로그 확인</strong> — 시스템 부하면 잠시 후 재시도</li>
        </ol>
      </section>

      <section id="faq" className="help-section">
        <h2>자주 묻는 질문</h2>
        <h3>업로드 도중 브라우저를 닫아도 안전한가요?</h3>
        <p>
          네 — 백엔드는 클라이언트 abort 를 감지하고 처리 중인 chunk 까지만 완료한 후 중단합니다. 응답의
          <code>aborted: true</code> 와 <code>successCount</code> 로 어디까지 처리됐는지 확인 가능합니다.
        </p>
        <h3>같은 CSV 를 다시 업로드해도 안전한가요?</h3>
        <p>
          네 — idempotent 합니다. 이미 존재하는 email 은 <code>EMAIL_EXISTS</code> 로 skip 되고 신규 행만
          생성됩니다.
        </p>
        <h3>password 미설정 사용자는 어떻게 로그인하나요?</h3>
        <p>
          별도 초기 비밀번호 발급 또는 invite 메일 절차가 필요합니다 (운영팀에 문의 또는 사용자가 직접 비밀번호 재설정 흐름 사용).
          미설정 상태에서는 login 이 차단됩니다.
        </p>
        <h3>audit 기록 실패 (auditPersisted=false) 가 표시되면 어떻게 하나요?</h3>
        <p>
          사용자 계정 자체는 정상 생성됐지만 audit log 영속화에 실패한 상태입니다. 운영팀에 server log 추적을
          요청하고, 작업 내역을 별도로 기록해두는 것이 권장됩니다.
        </p>
      </section>
    </article>
  );
}
